import type { FormEvent } from 'react'

import styled from 'styled-components'
import copy from 'copy.json'
import { DocumentIcon } from 'icons'

import Modal from 'components/Modal'
import Button from 'components/Button'
import Input from 'components/Input'
import FileUploadWrapper from 'components/layout/FileUploadWrapper'
import Spinner from 'components/Spinner'
import CsvFormatGuide from 'components/CsvFormatGuide'
import { individualPricingCsvGuide } from 'components/csvFormatGuides'

const text = copy.page.file_upload_confirmation

type Props = {
  isModalOpen: boolean
  onModalClose: () => void
  errors: string[]
  onHandleFileChange: (event: FormEvent<HTMLInputElement> | null) => void
  fileName?: string
  onModalContinue: () => void
  loading: boolean
}

export default function IndividualEnergyPricingErrorModal({
  isModalOpen,
  onModalClose,
  onHandleFileChange,
  onModalContinue,
  fileName,
  errors,
  loading
}: Props) {
  const handleCancelAdding = () => {
    onHandleFileChange(null)
    onModalClose()
  }

  const isError = errors?.length > 0

  return (
    <StyledModal isOpen={isModalOpen} onClose={onModalClose}>
      <h1>{text.heading_error}</h1>
      <Wrapper>
        <strong>{copy.page.search_matching_offers.individual_energy_pricing}</strong>

        <UploadedFileWrapper>
          <div>
            <DocumentIcon />
            {fileName}
          </div>
        </UploadedFileWrapper>

        {isError && (
          <Errors>
            <h2>{text.subheading_error}</h2>

            {errors.slice(0, 10).map((error, index) => (
              <div key={index}>{error}</div>
            ))}
          </Errors>
        )}

        <CsvFormatGuide definition={individualPricingCsvGuide} />

        <FileUploadWrapper>
          <Input name="file" type="file" onChange={onHandleFileChange} accept=".csv" />
          <Button secondary type="button" onClick={handleCancelAdding}>
            {copy.common.button.cancel_adding}
          </Button>
        </FileUploadWrapper>
      </Wrapper>
      <ButtonsWrapper>
        <Button disabled={isError} onClick={onModalContinue}>
          {loading ? <Spinner size={16} /> : copy.common.button.next}
        </Button>
        <Button secondary onClick={onModalClose}>
          {copy.common.button.close}
        </Button>
      </ButtonsWrapper>
    </StyledModal>
  )
}

const StyledModal = styled(Modal)`
  max-width: 868px;
  width: 100%;
`

const Errors = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px;
  margin-top: 20px;
  background: var(--very-light-blue);

  > h2 {
    font: var(--600-13);
    margin-bottom: 8px;
  }

  > div {
    padding: 8px 10px;
    border: 1px solid var(--light-gray);
    font: var(--400-12);
    color: var(--red);
  }
`

const Wrapper = styled.div`
  max-width: 648px;
  width: 100%;
  text-align: left;
  margin-bottom: 20px;
`

const ButtonsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-self: center;
  gap: 8px;
  width: 100%;
  max-width: 312px;
`

const UploadedFileWrapper = styled.div`
  margin-top: 20px;
  background: var(--very-light-blue);
  padding: 16px;

  > div {
    display: inline-flex;
    padding: 10px;
    gap: 16px;
    align-items: center;
    border: 1px solid var(--light-gray);
  }
`
